import React, { PureComponent, Fragment, useState, useEffect } from 'react';
import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import {
  Input,
  Button,
  Checkbox,
  Row,
  Col,
  message,
  Modal,
  Tooltip,
  Form,
  Radio,
  Space,
  Alert,
} from 'antd';
import { connect } from 'dva';
import Label from '@/components/Label/Label';
import ReconnectAuth from './ReconnectAuth';
import styles from './index.module.css';
import AwsSvgIcon from '@/../public/aws-1.svg';
import TestConnectionContent from '../ConfigurationTab/Components/TestConnectionContent';
import { ShieldOutlined } from '@ant-design/icons';
import CryptoJS from 'crypto-js';
import { createRoles } from '@/utils/createRoles';
import { nl2br } from '@/utils/utils';
import ScanRunningInstances from '../ConfigurationTab/Components/ScanRunningInstances';

const { info } = Modal;

const showPolicyModal = () => {
  info({
    title: 'AWS managed SecurityAudit Policy',
    footer: false,
    closable: true,
    destroyOnClose: true,
    width: 600,
    bodyStyle: { maxHeight: 'calc(100vh - 200px)', overflowY: 'scroll' },
    content: (
      <pre style={{ overflow: 'initial' }}>
        <code dangerouslySetInnerHTML={{ __html: nl2br(awsPolicy) }} />
      </pre>
    ),
  });
};

const data = [
  {
    title: 'AWS Login',
    description: () => {
      return <p>Log in to your AWS Account.</p>;
    },
    buttonText: 'Login to AWS',
    buttonKey: 'loginToAWS',
  },
  {
    title: 'Create IAM Roles and Policy',
    description: onViewPolicy => {
      return (
        <p>
          Create the required IAM Roles & Policy using the CloudFormation template.
          <br />
          <span
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#1890ff',
              cursor: 'pointer',
            }}
            onClick={onViewPolicy}
          >
            View Policy
          </span>
          <br />
          <br />
          Mark{' '}
          <b>
            &lt;&lt;I acknowledge that AWS CloudFormation might create IAM resources with custom
            names.&gt;&gt;
          </b>{' '}
          and click <b>Create Stack</b>
        </p>
      );
    },
    buttonText: 'Create IAM Roles',
    buttonKey: 'deployCft',
  },
];

const ReconnectCloudAccount = props => {
  const {
    cloudAccountData,
    updateData,
    deployStackButtonLoading,
    deployStackCheckboxDisabled,
    testConnectionTypes,
    updateProtecionLevelTo = cloudAccountData.protectionLevel || 'basic',
    onClose,
    isRolesMissing = false,
    isForceFullyReconnecting = false,
    deployCftButtonLoading,
    scanRunningInstances,
  } = props;

  const [deployedCftCheck, setDeployedCftCheck] = useState(false);

  const handledeployedCftCheck = isChecked => {
    setDeployedCftCheck(isChecked);
  };

  const isAdvancedAcc = updateProtecionLevelTo.toLowerCase() === 'advanced';

  const [showConnectionTesting, setShowConnectionTesting] = useState(false);
  const [policyModal, setPolicyModal] = useState(false);
  const [updatingAccount, setUpdatingAccount] = useState(false);

  const [form] = Form.useForm();

  // * Form on finish
  const onFinish = values => {
    setShowConnectionTesting(true);
    // updateData(values);
  };

  // * this function is used to validate the form before submit/update
  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields[0].name);
  };

  const getUrlToRedirect = type => {
    const formValues = form.getFieldsValue();
    const AWS_LOGIN_URL = 'https://console.aws.amazon.com/console/home?nc2=h_ct&src=header-signin';

    if (type === 'loginToAWS') {
      window.open(AWS_LOGIN_URL, '_blank').focus();
    }
    if (type === 'deployCft') {
      const { dispatch } = props;
      dispatch({
        type: 'cloudAccount/generateUrl',
        payload: {
          type,
          actionStatus: 'reconnecting',
          externalId: cloudAccountData.externalId,
          cxrRegion: cloudAccountData.regions[0],
          provider: 'aws',
        },
      });
    }
  };

  const handleDeployStack = region => {
    form.setFieldValue('region', region);
    // validate the form before submit
    form
      .validateFields()
      .then(values => {
        const payload = {
          type: 'deployStack',
          cxrRegion: region,
          roleArn: values.roleArn.trim(),
          cloudAccountId: cloudAccountData.id,
          externalId: cloudAccountData.externalId,
          orgId: cloudAccountData.orgId,
          authKey: cloudAccountData.cloudXrayConfig.authKey,
          receiverKey: cloudAccountData.cloudXrayConfig.receiverKey,
        };

        const { dispatch } = props;
        dispatch({
          type: 'cloudAccount/generateUrl',
          payload: {
            ...payload,
            actionStatus: 'reconnecting',
            provider: 'aws',
          },
        });
      })
      .catch(errorInfo => {
        form.scrollToField(errorInfo.errorFields[0].name);
      });
  };

  const getTestingData = () => {
    return {
      provider: cloudAccountData.provider,
      lastScan: cloudAccountData.lastScan,
      lastHealthCheck: cloudAccountData.lastHealthCheck,
      id: cloudAccountData.id,
      roleArn: form.getFieldValue('roleArn'),
      testApisOnly: false,
      protectionLevel: cloudAccountData.protectionLevel,
      externalId: cloudAccountData.externalId,
      cloudXrayConfig: {
        cxrRegion: form.getFieldValue('region') || cloudAccountData.cloudXrayConfig.cxrRegion,
        authKey: cloudAccountData.cloudXrayConfig.authKey,
      },
    };
  };

  const isAllConnectionConnected = () =>
    Object.keys(testConnectionTypes).every(key => testConnectionTypes[key].status === 200);

  const disabledFinishBtnTooltip = () => {
    // if any test is in loading then send loading tooltip
    const isConnectionsLoading = Object.keys(testConnectionTypes).some(
      key => testConnectionTypes[key].loading
    );
    if (isConnectionsLoading) return 'Please wait while we test your connection';

    // if any test is failed then send failed tooltip
    const isConnectionsFailed = Object.keys(testConnectionTypes).some(
      key => testConnectionTypes[key].status !== 200
    );
    if (isConnectionsFailed) return 'Please fix the failed connection';

    // if all test is success then send success tooltip with text finish reconnect and scan
    const isConnectionsSuccess = Object.keys(testConnectionTypes).every(
      key => testConnectionTypes[key].status === 200
    );
    if (isConnectionsSuccess) return 'Finish Reconnect & Scan';

    return '';
  };

  const encryptAES = text => {
    return CryptoJS.AES.encrypt(text, REACT_APP_SECRET).toString();
  };

  const handleFinishReconnectAndScan = async () => {
    //  ** update the cloud account data and then run scan on it
    const roles = createRoles(form.getFieldValue('roleArn').trim());
    const ROLE_ARNS = {
      execRoleArn: roles.execRoleARN,
      adminRoleArn: roles.adminRoleARN,
      roleArn: form.getFieldValue('roleArn').trim(),
    };

    //  ** encrypt the arns before updating
    const encryptedRoleArns = {
      execRoleArn: encryptAES(ROLE_ARNS.execRoleArn),
      adminRoleArn: encryptAES(ROLE_ARNS.adminRoleArn),
      roleArn: encryptAES(ROLE_ARNS.roleArn),
    };

    const payload = {
      ...cloudAccountData,
      ...encryptedRoleArns,
    };

    const { provider, orgId, id: cloudAccountId } = cloudAccountData;
    const { dispatch, userName } = props;

    // ** update the cloud account data
    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider,
          orgId,
          id: cloudAccountId,
        },
        data: {
          ...encryptedRoleArns,
          protectionLevel: updateProtecionLevelTo,
          updatedAt: new Date(),
          updatedBy: userName,
          isActive: true,
        },
      },
    });

    // ** start scan
    runScan(payload);

    // ** close modal
    onClose();
  };

  const runScan = accountData => {
    const { dispatch } = props;

    // Get currently scanning cloud accounts Ids
    let currentlyScanningCloudAccountsIds = getCurrentlyScanningCloudAccountsIds({
      shouldFilterWithStartScanStatus: true,
    });

    // Reset scan progress if currently no cloud account is scanning
    if (currentlyScanningCloudAccountsIds.length === 0) {
      dispatch({
        type: 'cloudAccount/clearScanProgress',
      });
    }

    dispatch({
      type: 'scan/runScan',
      payload: {
        cloudAccounts: [accountData],
        orgId: accountData.orgId,
        currentlyScanningCloudAccountsIds,
      },
    });

    dispatch({
      type: 'scopeReport/current',
    });
  };

  const getCurrentlyScanningCloudAccountsIds = ({ shouldFilterWithStartScanStatus = true }) => {
    const { allCloudAccountsList } = props;

    // Get currently scanning cloud accounts Ids
    const currentlyScanningCloudAccountsIds = allCloudAccountsList
      .filter(
        cloudAccount =>
          cloudAccount.status === 'running-scan' ||
          (shouldFilterWithStartScanStatus && cloudAccount.status === 'start-scan')
      )
      .map(cloudAccount => cloudAccount.id);

    return currentlyScanningCloudAccountsIds;
  };

  useEffect(() => {
    // * Unmount
    return () => {
      onUnMount();
    };
  }, []);

  const onUnMount = () => {
    setShowConnectionTesting(false);
    const { dispatch } = props;
    dispatch({
      type: 'cloudAccount/save',
      payload: {
        deployStackButtonLoading: false,
        deployStackCheckboxDisabled: true,
      },
    });
  };

  const updateAccountAndTestConnection = async () => {
    setUpdatingAccount(true);
    const { dispatch } = props;

    const payloadData = {
      cloudXrayConfig: {
        ...cloudAccountData.cloudXrayConfig,
        scanRunningInstances,
        updatedAt: new Date(),
      },
    };

    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider: cloudAccountData.provider,
          orgId: cloudAccountData.orgId,
          id: cloudAccountData.id,
        },
        data: payloadData,
      },
    });

    setUpdatingAccount(false);
    setShowConnectionTesting(true);
  };

  return (
    <>
      {!showConnectionTesting && (
        <Form onFinish={onFinish} form={form}>
          {isRolesMissing && (
            <Alert
              description="Oops! It seems like the required role/(s) for cloudxray deployment is/are missing in your aws account. Delete the stack named “Cloudnosys-Role” and then Reconnect your account with Cloudnosys platform, in order to switch into Advanced protection mode, by following these steps:"
              type="info"
              showIcon
              style={{
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            />
          )}

          <>
            {!isRolesMissing && (
              <div
                className={styles['cardMain']}
                style={{
                  color: 'red',
                  marginBottom: '20px',
                }}
              >
                {cloudAccountData.protectionLevel === 'advanced'
                  ? `Please make sure that you have deleted the stack named “cloudnosys-scanner-***” first
              and then the stack named “Cloudnosys-Security-Role”, before proceeding with the
              following steps to reconnect.`
                  : `Please make sure that you have deleted the stack named “Cloudnosys-Security-Role”, before proceeding with the following steps to reconnect.`}
              </div>
            )}

            <h5 style={{ marginBottom: '20px' }}>Create required roles</h5>
            <div className={styles['cardMain']}>
              {data.map((each, index) => {
                return (
                  <Row
                    gutter={[24, 0]}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Col span={16}>
                      <Row>
                        <Col span={2}>
                          <div
                            style={{
                              width: '26px',
                              height: '26px',
                              color: 'lightgrey',
                              borderRadius: '50%',
                              border: '1px solid lightgrey',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: '10px',
                            }}
                          >
                            {index + 1}
                          </div>
                        </Col>
                        <Col span={22}>
                          <h6>{each.title}</h6>
                          {each.description(() => setPolicyModal(true))}
                        </Col>
                      </Row>
                    </Col>
                    <Col
                      span={6}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        style={{ backgroundColor: 'transparent' }}
                        onClick={() => getUrlToRedirect(each.buttonKey)}
                        size="medium"
                        loading={each.buttonKey === 'deployCft' && deployCftButtonLoading}
                        disabled={each.buttonKey === 'deployCft' && deployCftButtonLoading}
                      >
                        <img
                          alt="Aws logo here"
                          style={{ width: 24, marginRight: 10 }}
                          src="/icons/aws_sm_icon.svg"
                        />
                        {each.buttonText}
                        <svg
                          style={{ marginLeft: '10px' }}
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.7583 0.0507812H9.48709C9.30628 0.0507812 9.15984 0.197222 9.15984 0.378027C9.15984 0.558659 9.30628 0.705099 9.48709 0.705099H12.7583C12.9041 0.705617 13.0478 0.73912 13.1787 0.80319L6.63889 7.34341C6.57569 7.40437 6.53977 7.48813 6.53908 7.57603C6.53821 7.66375 6.57275 7.74802 6.63475 7.81002C6.69692 7.87219 6.78119 7.90673 6.86892 7.90587C6.95665 7.90518 7.0404 7.86908 7.10136 7.80605L13.642 1.26627C13.7059 1.39717 13.7396 1.54085 13.7401 1.6866V4.95786C13.7401 5.13849 13.8866 5.28493 14.0672 5.28493C14.2478 5.28493 14.3943 5.13849 14.3943 4.95786V1.6866C14.3937 1.25297 14.2214 0.837128 13.9147 0.53063C13.608 0.223933 13.1923 0.0514089 12.7587 0.0508784L12.7583 0.0507812Z"
                            fill="#565656"
                          />
                          <path
                            d="M14.0664 6.5931C13.8858 6.5931 13.7394 6.73954 13.7394 6.92017V12.8088C13.7394 13.069 13.6359 13.3187 13.4518 13.5026C13.2679 13.6867 13.0182 13.7901 12.758 13.7901H6.5427V9.5375C6.54218 9.10387 6.36966 8.68803 6.06295 8.38153C5.75642 8.07483 5.34058 7.90231 4.90698 7.90178H0.654332V1.68649C0.654332 1.42624 0.757772 1.17654 0.941688 0.992456C1.12578 0.80854 1.37549 0.7051 1.63572 0.7051H7.52431C7.70494 0.7051 7.85138 0.558659 7.85138 0.378027C7.85138 0.197222 7.70494 0.0507812 7.52431 0.0507812H1.63572C1.20209 0.0512993 0.786251 0.223818 0.479752 0.530534C0.173055 0.837058 0.000530504 1.2529 0 1.6865V12.809C0.000518081 13.2426 0.173037 13.6582 0.479752 13.9649C0.786277 14.2716 1.20212 14.444 1.63572 14.4445H12.7582C13.1918 14.444 13.6075 14.2716 13.9141 13.9649C14.2208 13.6582 14.3932 13.2425 14.3937 12.809V6.92036C14.3937 6.83367 14.3593 6.75043 14.298 6.68913C14.2366 6.62765 14.1535 6.59328 14.0666 6.59328L14.0664 6.5931ZM0.653992 12.8084V8.55574H4.90664C5.16688 8.55574 5.41659 8.65901 5.60067 8.8431C5.78459 9.02719 5.88803 9.27672 5.88803 9.53713V13.7898H1.63538C1.37514 13.7898 1.12543 13.6863 0.941348 13.5023C0.757431 13.3183 0.653992 13.0686 0.653992 12.8084V12.8084Z"
                            fill="#565656"
                          />
                        </svg>
                      </Button>
                    </Col>
                  </Row>
                );
              })}
            </div>

            <h5 style={{ margin: '20px 0' }}>Connect with Cloudnosys</h5>
            <div className={styles['cardMain']}>
              <Row gutter={[24, 24]}>
                <Col span={12} lg={12} md={24}>
                  <p>
                    From the Output tab, copy the Role ARN created after deploying the above CFT and
                    paste it below:
                  </p>

                  <FormInput
                    label="Cloudnosys Role ARN (RoleARN)"
                    name="roleArn"
                    errMessage="Please enter your AWS Role ARN"
                  />
                </Col>
                <Col span={12} lg={12} md={24}>
                  <img
                    src="/roleArns_snippet.png"
                    alt="roleArn snippet here"
                    style={{
                      borderRadius: '8px',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      boxShadow: '0 2px 4px rgb(0 0 0 / 10%), 0 8px 16px rgb(0 0 0 / 10%)',
                    }}
                  />
                </Col>
              </Row>
            </div>
          </>

          {updateProtecionLevelTo.toLowerCase() === 'advanced' && (
            <>
              <h5 style={{ margin: '20px 0' }}>Create Stack</h5>
              <div className={styles['cardMain']}>
                <ReconnectAuth
                  handledeployedCftCheck={handledeployedCftCheck}
                  handleDeployStack={handleDeployStack}
                  deployStackButtonLoading={deployStackButtonLoading}
                  deployStackCheckboxDisabled={deployStackCheckboxDisabled}
                />
              </div>
            </>
          )}

          <ScanRunningInstances deployedCftCheck={deployedCftCheck} />

          <span
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: '10px',
            }}
          >
            <Tooltip
              placement="bottom"
              title={
                isAdvancedAcc && deployStackCheckboxDisabled ? 'Please deploy CFT first' : null
              }
            >
              <Button
                type="primary"
                htmlType="button"
                loading={updatingAccount}
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      updateAccountAndTestConnection();
                    })
                    .catch(errorInfo => {
                      console.log('errorInfo from validateFields catch: ', errorInfo);
                    });
                }}
                disabled={isAdvancedAcc ? deployStackCheckboxDisabled : false}
              >
                Test Connection
              </Button>
            </Tooltip>
          </span>
        </Form>
      )}

      {showConnectionTesting && (
        <>
          <TestConnectionContent
            cloudAccountData={getTestingData()}
            byPassAndTestAll={true}
            isEditCloudAccount={isForceFullyReconnecting ? false : true}
            connectionProtectionType={isAdvancedAcc ? 'advanced' : 'basic'}
          />
          <Tooltip placement="bottom" title={disabledFinishBtnTooltip()}>
            <Button
              type="primary"
              style={{ float: 'right' }}
              disabled={!isAllConnectionConnected()}
              onClick={handleFinishReconnectAndScan}
              loading={props.updating}
            >
              Finish Reconnect & Scan
            </Button>
          </Tooltip>
        </>
      )}

      {policyModal && <PolicyModal policyModal={policyModal} setPolicyModal={setPolicyModal} />}
    </>
  );
};

// export default ReconnectCloudAccount;
export default connect(({ user, cloudAccount }) => ({
  updating: cloudAccount.updating,
  scanRunningInstances: cloudAccount.scanRunningInstances,
  deployStackButtonLoading: cloudAccount.deployStackButtonLoading,
  deployStackCheckboxDisabled: cloudAccount.deployStackCheckboxDisabled,
  testConnectionTypes: cloudAccount.testConnectionTypes,
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
  deployCftButtonLoading: cloudAccount.deployCftButtonLoading,
  userName: user.currentUser.name,
}))(ReconnectCloudAccount);

const FormInput = ({ label, name, errMessage }) => {
  return (
    <>
      <p className={styles['inputLabel']}>{label}</p>
      <Form.Item
        required
        name={name}
        rules={[
          {
            required: true,
            message: errMessage,
            // field must have length greater than 20
          },
        ]}
        style={{
          marginBottom: 0,
        }}
      >
        <Input placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
      </Form.Item>
    </>
  );
};

const PolicyModal = ({ policyModal, setPolicyModal }) => {
  return (
    <Modal
      open={policyModal}
      title="AWS managed SecurityAudit Policy"
      footer={false}
      closable={true}
      destroyOnClose
      width={600}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'scroll' }}
      onCancel={() => setPolicyModal(false)}
    >
      <pre style={{ overflow: 'initial' }}>
        <code dangerouslySetInnerHTML={{ __html: nl2br(awsPolicy) }} />
      </pre>
    </Modal>
  );
};

export const awsPolicy = `{
  "Version": "2012-10-17T00:00:00.000Z",
  "Statement": [
    {
      "Effect": "Allow",
      "Resource": "*",
      "Action": [
        "acm:Describe*",
        "apigateway:*",
        "acm:List*",
        "application-autoscaling:Describe*",
        "appmesh:Describe*",
        "appmesh:List*",
        "appsync:List*",
        "athena:List*",
        "autoscaling:Describe*",
        "batch:DescribeComputeEnvironments",
        "batch:DescribeJobDefinitions",
        "chime:List*",
        "cloud9:Describe*",
        "cloud9:ListEnvironments",
        "clouddirectory:ListDirectories",
        "cloudformation:DescribeStack*",
        "cloudformation:GetTemplate",
        "cloudformation:ListStack*",
        "cloudformation:GetStackPolicy",
        "cloudfront:Get*",
        "cloudfront:List*",
        "cloudhsm:ListHapgs",
        "cloudhsm:ListHsms",
        "cloudhsm:ListLunaClients",
        "cloudsearch:DescribeDomains",
        "cloudsearch:DescribeServiceAccessPolicies",
        "cloudtrail:DescribeTrails",
        "cloudtrail:GetEventSelectors",
        "cloudtrail:GetTrailStatus",
        "cloudtrail:ListTags",
        "cloudtrail:LookupEvents",
        "cloudwatch:Describe*",
        "codebuild:ListProjects",
        "codecommit:BatchGetRepositories",
        "codecommit:GetBranch",
        "codecommit:GetObjectIdentifier",
        "codecommit:GetRepository",
        "codecommit:List*",
        "codedeploy:Batch*",
        "codedeploy:Get*",
        "codedeploy:List*",
        "codepipeline:ListPipelines",
        "codestar:Describe*",
        "codestar:List*",
        "cognito-identity:ListIdentityPools",
        "cognito-idp:ListUserPools",
        "cognito-sync:Describe*",
        "cognito-sync:List*",
        "comprehend:Describe*",
        "comprehend:List*",
        "config:Deliver*",
        "config:Describe*",
        "config:Get*",
        "datapipeline:DescribeObjects",
        "datapipeline:DescribePipelines",
        "datapipeline:EvaluateExpression",
        "datapipeline:GetPipelineDefinition",
        "datapipeline:ListPipelines",
        "datapipeline:QueryObjects",
        "datapipeline:ValidatePipelineDefinition",
        "datasync:Describe*",
        "datasync:List*",
        "dax:Describe*",
        "dax:ListTags",
        "directconnect:Describe*",
        "dms:Describe*",
        "dms:ListTagsForResource",
        "ds:DescribeDirectories",
        "dynamodb:DescribeContinuousBackups",
        "dynamodb:DescribeGlobalTable",
        "dynamodb:DescribeTable",
        "dynamodb:DescribeTimeToLive",
        "dynamodb:ListBackups",
        "dynamodb:ListGlobalTables",
        "dynamodb:ListStreams",
        "dynamodb:ListTables",
        "ec2:Describe*",
        "ecr:DescribeRepositories",
        "ecr:GetRepositoryPolicy",
        "ecs:Describe*",
        "ecs:List*",
        "eks:DescribeCluster",
        "eks:ListClusters",
        "elasticache:Describe*",
        "elasticbeanstalk:Describe*",
        "elasticfilesystem:DescribeFileSystems",
        "elasticloadbalancing:Describe*",
        "elasticmapreduce:Describe*",
        "elasticmapreduce:ListClusters",
        "elasticmapreduce:ListInstances",
        "es:Describe*",
        "es:ListDomainNames",
        "events:DescribeEventBus",
        "events:ListRules",
        "firehose:Describe*",
        "firehose:List*",
        "fsx:Describe*",
        "fsx:List*",
        "gamelift:ListBuilds",
        "gamelift:ListFleets",
        "glacier:DescribeVault",
        "glacier:GetVaultAccessPolicy",
        "glacier:ListVaults",
        "globalaccelerator:Describe*",
        "globalaccelerator:List*",
        "greengrass:List*",
        "guardduty:Get*",
        "guardduty:List*",
        "iam:GenerateCredentialReport",
        "iam:Get*",
        "iam:List*",
        "iam:SimulateCustomPolicy",
        "iam:SimulatePrincipalPolicy",
        "iot:Describe*",
        "iot:List*",
        "kinesis:DescribeStream",
        "kinesis:ListStreams",
        "kinesis:ListTagsForStream",
        "kinesisanalytics:ListApplications",
        "kms:Describe*",
        "kms:Get*",
        "kms:List*",
        "lambda:GetAccountSettings",
        "lambda:GetPolicy",
        "lambda:List*",
        "license-manager:List*",
        "logs:Describe*",
        "logs:ListTagsLogGroup",
        "machinelearning:DescribeMLModels",
        "mediaconnect:Describe*",
        "mediaconnect:List*",
        "mediastore:GetContainerPolicy",
        "mediastore:ListContainers",
        "opsworks-cm:DescribeServers",
        "organizations:List*",
        "organizations:Describe*",
        "quicksight:Describe*",
        "quicksight:List*",
        "ram:List*",
        "rds:Describe*",
        "rds:DownloadDBLogFilePortion",
        "rds:ListTagsForResource",
        "redshift:Describe*",
        "rekognition:Describe*",
        "rekognition:List*",
        "robomaker:Describe*",
        "robomaker:List*",
        "route53:Get*",
        "route53:List*",
        "route53domains:GetDomainDetail",
        "route53domains:GetOperationDetail",
        "route53domains:ListDomains",
        "route53domains:ListOperations",
        "route53domains:ListTagsForDomain",
        "route53resolver:List*",
        "s3:GetAccelerateConfiguration",
        "s3:GetAnalyticsConfiguration",
        "s3:GetBucket*",
        "s3:GetEncryptionConfiguration",
        "s3:GetInventoryConfiguration",
        "s3:GetLifecycleConfiguration",
        "s3:GetMetricsConfiguration",
        "s3:GetObjectAcl",
        "s3:GetObjectVersionAcl",
        "s3:GetReplicationConfiguration",
        "s3:ListAllMyBuckets",
        "sagemaker:Describe*",
        "sagemaker:List*",
        "sdb:DomainMetadata",
        "sdb:ListDomains",
        "securityhub:Get*",
        "securityhub:List*",
        "serverlessrepo:GetApplicationPolicy",
        "serverlessrepo:List*",
        "ses:GetIdentityDkimAttributes",
        "ses:GetIdentityVerificationAttributes",
        "ses:ListIdentities",
        "ses:ListVerifiedEmailAddresses",
        "shield:Describe*",
        "shield:List*",
        "snowball:ListClusters",
        "snowball:ListJobs",
        "sns:GetTopicAttributes",
        "sns:ListSubscriptionsByTopic",
        "sns:ListTopics",
        "sqs:GetQueueAttributes",
        "sqs:ListQueues",
        "ssm:Describe*",
        "ssm:ListDocuments",
        "states:ListStateMachines",
        "storagegateway:DescribeBandwidthRateLimit",
        "storagegateway:DescribeCache",
        "storagegateway:DescribeCachediSCSIVolumes",
        "storagegateway:DescribeGatewayInformation",
        "storagegateway:DescribeMaintenanceStartTime",
        "storagegateway:DescribeNFSFileShares",
        "storagegateway:DescribeSnapshotSchedule",
        "storagegateway:DescribeStorediSCSIVolumes",
        "storagegateway:DescribeTapeArchives",
        "storagegateway:DescribeTapeRecoveryPoints",
        "storagegateway:DescribeTapes",
        "storagegateway:DescribeUploadBuffer",
        "storagegateway:DescribeVTLDevices",
        "storagegateway:DescribeWorkingStorage",
        "storagegateway:List*",
        "tag:GetResources",
        "tag:GetTagKeys",
        "tag:GetTagValues",
        "transfer:Describe*",
        "transfer:List*",
        "translate:List*",
        "trustedadvisor:Describe*",
        "waf:ListWebACLs",
        "waf-regional:ListWebACLs",
        "workspaces:Describe*"
      ]
    },
    {
      "Effect": "Allow",
      "Resource": [
        "arn:aws:apigateway:*::/restapis",
        "arn:aws:apigateway:*::/restapis/*/authorizers",
        "arn:aws:apigateway:*::/restapis/*/authorizers/*",
        "arn:aws:apigateway:*::/restapis/*/resources",
        "arn:aws:apigateway:*::/restapis/*/resources/*",
        "arn:aws:apigateway:*::/restapis/*/resources/*/methods/*",
        "arn:aws:apigateway:*::/vpclinks"
      ],
      "Action": [
        "apigateway:GET"
      ]
    }
  ]
}`;
